import { useCallback, useEffect, useRef, useState } from 'react';
import type { ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';

export interface FrameHandler {
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  dispose(): void;
}

export const useFrameHandler = <T extends FrameHandler>(
  enabled: boolean,
  create: () => T
) => {
  const createRef = useRef(create);
  createRef.current = create;
  const instanceRef = useRef<T | null>(null);
  const [instance, setInstance] = useState<T | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const next = createRef.current();
    instanceRef.current = next;
    setInstance(next);
    return () => {
      next.dispose();
      instanceRef.current = null;
      setInstance(null);
    };
  }, [enabled]);

  const handle = useCallback(
    (message: ServerMessage, meta: PiChatFrameMeta) =>
      instanceRef.current?.handleServerMessage(message, meta),
    []
  );

  return { instance, handle };
};
